'use client';

import React, { useState, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { UserSidebar } from '@/components/layout/UserSidebar';
import { Topbar } from '@/components/layout/Topbar';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Loader2, Mail, LogOut, RefreshCw, AlertCircle, ShieldAlert } from 'lucide-react';
import { authApi } from '@/sdk/shared/auth';
import { apiClient } from '@/sdk/shared/apiClient';
import { userNavItems } from '@/components/layout/nav-config';
import { toast } from 'sonner';
import { PushNotificationManager } from '@/components/providers/PushNotificationManager';
import { restoreAdminSessionAndReturn } from '@/sdk/shared/auth/utils';
import { cn } from '@/lib/utils';
import { PullToRefresh } from '@/components/ui/pull-to-refresh';

export default function UserLayout({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const mainRef = React.useRef<HTMLElement>(null);

  // ── All hooks MUST be declared before any conditional return (Rules of Hooks) ──
  const { user, userType, updateUser, clearAuth } = useAuth();
  const [isChecking, setIsChecking] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Force login impersonation audit state
  const [impersonationInfo, setImpersonationInfo] = useState<{
    sessionId: string | null;
    adminName: string;
    adminEmail: string;
  } | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const forceToken = sessionStorage.getItem('force_auth_token');
    const sessionId = sessionStorage.getItem('force_auth_session_id');
    const adminStr = sessionStorage.getItem('force_auth_admin');

    if (forceToken) {
      let adminName = 'Admin';
      let adminEmail = 'admin@crm.com';
      if (adminStr) {
        try {
          const parsed = JSON.parse(adminStr);
          adminName = parsed.name || adminName;
          adminEmail = parsed.email || adminEmail;
        } catch {}
      }
      setImpersonationInfo({ sessionId, adminName, adminEmail });
    }
  }, []);

  // Track page views on route changes for impersonated sessions
  useEffect(() => {
    // Skip on login page (early return below handles that render path)
    if (pathname?.startsWith('/client/login')) return;
    if (typeof window === 'undefined') return;
    const forceToken = sessionStorage.getItem('force_auth_token');
    const sessionId = sessionStorage.getItem('force_auth_session_id');

    if (forceToken && pathname) {
      const navItem = userNavItems.find(item => item.href === pathname);
      const pageTitle = navItem ? navItem.label : pathname.replace('/client/', '').replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

      apiClient.post('/client/audit/impersonation-page-view', {
        sessionId,
        route: pathname,
        title: pageTitle,
        metadata: { search: window.location.search }
      }).catch(() => {});
    }
  }, [pathname]);

  // Track unload / session termination for impersonated sessions
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleUnload = () => {
      const forceToken = sessionStorage.getItem('force_auth_token');
      const sessionId = sessionStorage.getItem('force_auth_session_id');
      const startedAt = sessionStorage.getItem('force_auth_started_at');

      if (forceToken && sessionId) {
        let durationSeconds = 0;
        if (startedAt) {
          durationSeconds = Math.round((Date.now() - new Date(startedAt).getTime()) / 1000);
        }
        const payload = JSON.stringify({ sessionId, durationSeconds });
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon?.('/api/client/audit/impersonation-logout', blob);
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // Authoritative Client Access Gate check
  const [isAccessGateChecking, setIsAccessGateChecking] = useState(true);
  const [accessGate, setAccessGate] = useState<any>(null);

  useEffect(() => {
    let isMounted = true;
    if (userType === 'client') {
      authApi.getClientAccessStatus()
        .then((res: any) => {
          if (!isMounted) return;
          setAccessGate(res);
          setIsAccessGateChecking(false);
          if (res?.allowed || (res?.missingFields && res.missingFields.length === 0)) {
            if (user && (!user.emailVerified || user.status === 'incomplete')) {
              updateUser({ emailVerified: true, status: 'active' });
            }
          } else if (res?.missingFields && res.missingFields.length > 0) {
            router.replace('/register');
          }
        })
        .catch((err: any) => {
          if (!isMounted) return;
          console.warn('[UserLayout] Client access check error:', err);
          setIsAccessGateChecking(false);
          if (err?.error === 'REGISTRATION_INCOMPLETE' || err?.code === 'REGISTRATION_INCOMPLETE') {
            router.replace('/register');
          }
        });
    } else {
      setIsAccessGateChecking(false);
    }
    return () => {
      isMounted = false;
    };
  }, [userType, router]);

  const termsAccepted = Boolean(
    user?.termsAndConditionsAccepted ||
    user?.terms_and_conditions_accepted ||
    user?.termsAndConditions ||
    (user as any)?.terms_and_conditions
  );

  const hasAllProfileFields = Boolean(
    user &&
    (user.firstName || user.first_name) &&
    (user.lastName || user.last_name) &&
    (user.dateOfBirth || user.dob || user.date_of_birth) &&
    user.city &&
    user.country &&
    termsAccepted
  );

  // Synchronous client data completeness check - only redirect if mandatory fields are missing
  const isProfileIncomplete = Boolean(
    userType === 'client' && user && !accessGate?.allowed && !hasAllProfileFields
  );

  useEffect(() => {
    if (isProfileIncomplete && !isAccessGateChecking) {
      router.replace('/register');
    }
  }, [isProfileIncomplete, isAccessGateChecking, router]);

  // ── Conditional returns AFTER all hooks ──

  // If on client login page, do not wrap with sidebar/topbar
  if (pathname?.startsWith('/client/login')) {
    return <>{children}</>;
  }

  // Prevent dashboard flashing: render skeleton/loader while checking access gate
  if (userType === 'client' && isAccessGateChecking && !hasAllProfileFields) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground animate-pulse">Verifying client access permissions...</p>
        </div>
      </div>
    );
  }

  // If client is logged in but email is not verified, only show verification UI if profile fields are not complete
  const isEmailUnverified = Boolean(
    userType === 'client' &&
    user &&
    !hasAllProfileFields &&
    !accessGate?.allowed &&
    (user.emailVerified === false || (accessGate && accessGate.emailVerified === false))
  );
  if (isEmailUnverified) {
    const handleCheckVerification = async () => {
      setIsChecking(true);
      try {
        const response = await authApi.getMe();
        if (response?.client?.emailVerified) {
          toast.success('Email verified successfully! Welcome to your dashboard.');
          
          // Re-serialize user cookie with emailVerified: true
          updateUser({ emailVerified: true, status: 'active' });
          setAccessGate((prev: any) => prev ? { ...prev, allowed: true, emailVerified: true, status: 'COMPLETE' } : null);
        } else {
          toast.error('Email is not verified yet. Please check your inbox.');
        }
      } catch (err: any) {
        console.error('Check verification failed:', err);
        toast.error(err?.error || 'Verification check failed. Please try again.');
      } finally {
        setIsChecking(false);
      }
    };

    const handleResend = async () => {
      setIsResending(true);
      try {
        await authApi.resendVerification(user?.email);
        toast.success('Verification link sent successfully!');
      } catch (err: any) {
        toast.error(err?.error || 'Failed to resend verification email.');
      } finally {
        setIsResending(false);
      }
    };

    const handleLogout = () => {
      const tenantSlug = user?.tenant?.slug || (typeof window !== 'undefined' ? localStorage.getItem('last_tenant_slug') : null);
      clearAuth();
      const slugQuery = tenantSlug ? `?slug=${encodeURIComponent(tenantSlug)}` : '';
      window.location.href = `/client/login${slugQuery}`;
    };

    const handleReturnToAdmin = () => {
      restoreAdminSessionAndReturn('/admin/clients');
    };

    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 w-full">
        {impersonationInfo && (
          <div className="bg-amber-500/15 border-b border-amber-500/30 text-amber-900 dark:text-amber-200 px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 text-xs sm:text-sm font-medium z-30 shrink-0">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 animate-pulse" />
              <span>
                <strong>Admin Force-Login Active:</strong> Viewing account of <span className="font-semibold">{user?.email}</span> (Audited by: {impersonationInfo.adminName}).
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-amber-500/40 hover:bg-amber-500/20 text-foreground font-semibold"
              onClick={handleReturnToAdmin}
            >
              <LogOut className="h-3.5 w-3.5 mr-1" />
              Return to Admin Portal
            </Button>
          </div>
        )}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md bg-card border border-border shadow-2xl rounded-2xl p-8 text-center transition-all duration-300">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400 mb-6">
              <Mail className="h-8 w-8 animate-pulse" />
            </div>

            <h1 className="text-2xl font-bold tracking-tight mb-2 text-foreground">
              Verify Your Email
            </h1>
            
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              Welcome aboard! We sent a secure verification email to <br />
              <span className="font-semibold text-foreground">{user?.email}</span>. <br />
              Please click the link inside that email to activate your dashboard.
            </p>

            <div className="space-y-3 mb-6">
              <Button
                onClick={handleCheckVerification}
                disabled={isChecking}
                className="w-full h-11 font-semibold flex items-center justify-center gap-2"
              >
                {isChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                I've Verified My Email
              </Button>

              <Button
                variant="outline"
                onClick={handleResend}
                disabled={isResending}
                className="w-full h-11 font-semibold flex items-center justify-center gap-2 border-muted hover:bg-muted/50"
              >
                {isResending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Resend Verification Link
              </Button>
            </div>

            <div className="border-t border-border pt-4 flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-1 text-amber-500 font-medium">
                <AlertCircle className="h-3.5 w-3.5" />
                Access restricted
              </div>
              <button
                onClick={handleLogout}
                className="font-semibold text-primary hover:underline flex items-center gap-1 cursor-pointer"
              >
                <LogOut className="h-3.5 w-3.5" />
                Logout / Switch Account
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleReturnToAdmin = () => {
    restoreAdminSessionAndReturn('/admin/clients');
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-gradient-to-br from-[#D2F5E3] via-[#EAF9F1] to-[#D8F6E6] dark:from-[#0B1524] dark:via-[#0E1A29] dark:to-[#0A1420]">
      <PushNotificationManager />
      {/* Desktop sidebar */}
      <UserSidebar className="hidden md:flex" />

      {/* Main area */}
      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        {impersonationInfo && (
          <div className="bg-amber-500/15 border-b border-amber-500/30 text-amber-900 dark:text-amber-200 px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-xs sm:text-sm font-medium z-30 shrink-0">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 animate-pulse" />
              <span>
                <strong>Admin Force-Login Active:</strong> Viewing account of <span className="font-semibold">{user?.email}</span> (Audited by: {impersonationInfo.adminName} &lt;{impersonationInfo.adminEmail}&gt;). All views and actions are recorded.
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-amber-500/40 hover:bg-amber-500/20 text-foreground font-semibold"
              onClick={handleReturnToAdmin}
            >
              <LogOut className="h-3.5 w-3.5 mr-1" />
              Return to Admin Portal
            </Button>
          </div>
        )}
        <Topbar />
        
        {/* Page Content */}
        <main
          ref={mainRef}
          className={cn(
            "relative z-0 flex-1 overflow-y-auto animate-in fade-in-0 duration-300 pb-safe pb-[calc(1rem+env(safe-area-inset-bottom,0px))]",
            pathname === '/client/ai-chat' ? "p-0 overflow-hidden flex flex-col" : "p-4 md:p-6"
          )}
        >
          {pathname === '/client/ai-chat' ? (
            <div className="w-full h-full flex flex-col">
              {children}
            </div>
          ) : (
            <PullToRefresh containerRef={mainRef}>
              <div className="w-full">
                {children}
              </div>
            </PullToRefresh>
          )}
        </main>
      </div>
    </div>
  );
}

